import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './contexts/LanguageContext';

function ThankYou() {
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const timeoutRef = useRef(null);

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const resetTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        navigate('/');
      }, 5000);
    };

    resetTimer();

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach((event) => {
      window.addEventListener(event, resetTimer, true);
    });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer, true);
      });
    };
  }, [navigate]);

  return (
    <div className={`bg-beige-medium min-h-dvh flex justify-center items-center flex-col relative ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 xl:top-10 xl:left-10 z-10">
        <button
          className="bg-transparent border-none cursor-pointer w-9 md:w-12 text-brown-dark flex items-center justify-center transition-opacity duration-200 hover:opacity-70"
          onClick={() => navigate('/')}
          aria-label={translations.labels.goHome}
        >
          <svg className='w-full h-full' width="88" height="79" viewBox="0 0 88 79" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M87.064 30.9834L45.3509 0.441675C44.5465 -0.147225 43.4537 -0.147225 42.6497 0.441675L0.936001 30.9834C-0.0828986 31.7295 -0.3043 33.1602 0.4418 34.179C1.1879 35.1979 2.6188 35.4189 3.6375 34.6732L44.0001 5.12028L84.363 34.673C84.77 34.9712 85.243 35.1147 85.712 35.1147C86.416 35.1147 87.11 34.7907 87.558 34.1789C88.304 33.1602 88.083 31.7295 87.064 30.9834Z" fill="#89371D" />
            <path d="M76.011 35.1133C74.748 35.1133 73.725 36.1369 73.725 37.3997V73.7939H55.4332V53.9277C55.4332 47.6234 50.3041 42.4946 44.0001 42.4946C37.6961 42.4946 32.567 47.6237 32.567 53.9277V73.7939H14.2755V37.3999C14.2755 36.1371 13.2517 35.1134 11.9891 35.1134C10.7265 35.1134 9.70258 36.1371 9.70258 37.3999V76.0809C9.70258 77.3439 10.7265 78.3669 11.9891 78.3669H34.8534C36.0559 78.3669 37.0399 77.4379 37.131 76.2589C37.1365 76.2059 37.1399 76.1469 37.1399 76.0809V53.9279C37.1399 50.1451 40.2173 47.0677 44.0001 47.0677C47.7829 47.0677 50.8603 50.1453 50.8603 53.9279V76.0809C50.8603 76.1469 50.8638 76.2039 50.8693 76.2569C50.9593 77.4369 51.9437 78.3669 53.1468 78.3669H76.011C77.274 78.3669 78.298 77.3439 78.298 76.0809V37.3999C78.297 36.1369 77.274 35.1133 76.011 35.1133Z" fill="#89371D" />
          </svg>
        </button>
      </div>
      <img className="absolute top-0 left-0 w-full h-full object-cover opacity-[0.03]" src="/images/bg-pattern-1.webp" alt="pattern" width="100%" height="100%" />
      <img className="absolute top-0 left-0 w-full h-full object-cover opacity-[0.03]" src="/images/bg-pattern-2.webp" alt="pattern" width="100%" height="100%" />
      <div className="bg-beige-medium/70 backdrop-blur-[1px] relative rounded-[70%] text-center py-8 sm:py-10 px-6 sm:px-10 xl:px-[80px] w-full max-w-[1020px] my-auto z-10">
        <div className="w-[260px] xl:w-[280px] mx-auto aspect-[280/228] relative">
          <img className="w-full h-full object-contain" src="/images/logo.svg" alt="logo" width="280" height="228" loading="eager" />
        </div>
        <h1 className="text-brown-primary font-[ArtifexHandCF] text-[40px] sm:text-[56px] xl:text-[72px] leading-[132%] font-semibold mt-8 sm:mt-10 xl:mt-12">{translations.thankYou.title}</h1>
        <p className="text-[18px] sm:text-[22px] xl:text-[28px] leading-[120%] text-dark-shade1 mt-4 mx-auto max-w-[960px] whitespace-pre-line">{translations.thankYou.description}</p>
        {/* <button className="bg-transparent border-2 border-brown-primary font-[Forum] text-brown-primary text-[60px] leading-[120%] mt-[110px] xl:mt-[120px] py-3 rounded-full cursor-pointer h-[160px] min-w-[670px] transition-all duration-300 ease-in-out hover:bg-brown-primary hover:text-white" onClick={() => navigate('/')}>{translations.thankYou.backToHome}</button> */}
      </div>

      <div className="mt-6 md:mt-10 px-4 sm:px-8 pb-8 relative z-10 max-xl:mb-auto w-full">
        <div className="flex items-center justify-center gap-5">
          <svg className="w-[70px] h-auto" width="108" height="3" viewBox="0 0 108 3" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="54" cy="1.5" rx="54" ry="1.5" fill="#3A3A3A" />
          </svg>
          <h2 className="text-brown-primary text-[20px] sm:text-[26px] xl:text-[30px] leading-[120%] whitespace-nowrap">
            {translations.thankYou.partners}
          </h2>
          <svg className="w-[70px] h-auto" width="108" height="3" viewBox="0 0 108 3" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="54" cy="1.5" rx="54" ry="1.5" fill="#3A3A3A" />
          </svg>
        </div>
        <div className="bg-beige-light/80 rounded-xl p-4 sm:p-6 mt-4 md:mt-8">
          <div className="flex flex-col xl:flex-row gap-10 md:gap-[70px]">
            {/* Main partner logos - two rows */}
            <div className="flex-1 flex flex-wrap justify-center items-center gap-5 md:gap-8">
              {/* Top Row */}
              <div className="flex items-center justify-center h-[40px] md:h-[50px] min-w-[150px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/bm.webp" alt="The British Museum" className="max-w-full max-h-full w-auto h-auto object-contain" width="150" height="50" />
              </div>
              
              <div className="flex items-center justify-center h-[45px] md:h-[55px] min-w-[280px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/smb.webp" alt="Staatliche Museen zu Berlin" className="max-w-full max-h-full w-auto h-auto object-contain" width="280" height="55" />
              </div>
              <div className="flex items-center justify-center h-[75px] min-w-[183px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/csmvs.webp" alt="CSMVS" className="max-w-full max-h-full w-auto h-auto object-contain" width="183" height="85" />
              </div>
              <div className="flex items-center justify-center h-[75px] min-w-[75px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/archaeological-survey-of-india.webp" alt="Archaeological Survey of India" className="max-w-full max-h-full w-auto h-auto object-contain" width="75" height="85" />
              </div>
              <div className="flex items-center justify-center h-[45px] md:h-[55px] min-w-[161px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/rietberg-museum.webp" alt="Museum Rietberg" className="max-w-full max-h-full w-auto h-auto object-contain" width="161" height="55" />
              </div>
              <div className="flex items-center justify-center h-[90px] md:h-[110px] min-w-[81px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/national-museum-delhi.webp" alt="National Museum New Delhi" className="max-w-full max-h-full w-auto h-auto object-contain" width="81" height="110" />
              </div>
              <div className="flex items-center justify-center h-[40px] md:h-[50px] min-w-[211px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/benaki-museum.webp" alt="Benaki Museum" className="max-w-full max-h-full w-auto h-auto object-contain" width="211" height="50" />
              </div>
              <div className="flex items-center justify-center h-[90px] md:h-[120px] min-w-[120px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/museum.webp" alt="Ephorate of Antiquities of the City of Athens" className="max-w-full max-h-full w-auto h-auto object-contain" width="120" height="120" />
              </div>
              <div className="flex items-center justify-center h-[80px] md:h-[91px] min-w-[140px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/ephorate.webp" alt="Ephorate of Antiquities of the City of Athens" className="max-w-full max-h-full w-auto h-auto object-contain" width="140" height="91" />
              </div>
              <div className="flex items-center justify-center h-[73px] min-w-[140px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/bihar-museum.webp" alt="Bihar Museum" className="max-w-full max-h-full w-auto h-auto object-contain" width="140" height="73" />
              </div>
              <div className="flex items-center justify-center h-[90px] md:h-[100px] min-w-[100px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/al-sabah-kuwait.webp" alt="Dar al-Athar al-Islamiyyah" className="max-w-full max-h-full w-auto h-auto object-contain" width="100" height="100" />
              </div>
              <div className="flex items-center justify-center h-[90px] md:h-[100px] min-w-[100px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/indian-museum.webp" alt="Indian Museum Kolkata" className="max-w-full max-h-full w-auto h-auto object-contain" width="100" height="100" />
              </div>
              <div className="flex items-center justify-center h-[90px] md:h-[116px] min-w-[100px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/allahabad-museum.webp" alt="Allahabad Museum" className="max-w-full max-h-full w-auto h-auto object-contain" width="100" height="116  " />
              </div>
              <div className="flex items-center justify-center h-[90px] md:h-[100px] min-w-[100px]">
                <img loading="eager" fetchPriority="high" src="/images/partners-logos/seal-of-uttar-pradesh.webp" alt="Uttar Pradesh Government" className="max-w-full max-h-full w-auto h-auto object-contain" width="100" height="100" />
              </div>
            </div>
            
            {/* Supported By Section */}
            <div className="flex flex-col max-xl:items-center gap-0 md:gap-10 xl:gap-[55px]">
              <h5 className="text-dark-shade1 text-[20px] md:text-[24px] leading-[120%] font-normal mb-5 pt-5">
                SUPPORTED BY
              </h5>
              <div className="flex gap-5 md:gap-[64px]">
                <div className="flex items-center justify-center h-[50px] md:h-[70px] min-w-[171px] relative max-xl:after:hidden after:content-[''] after:absolute after:top-1/2 after:translate-y-[-50%] after:-left-9 after:w-0.5 after:h-[130px] after:bg-dark-shade1">
                  <img loading="eager" fetchPriority="high" src="/images/partners-logos/getty.webp" alt="Getty" className="max-w-full max-h-full w-auto h-auto object-contain" width="171" height="70" />
                </div>
                <div className="flex items-center justify-centerh-[50px] md:h-[70px] min-w-[136px] relative after:content-[''] after:absolute after:top-1/2 after:translate-y-[-50%] after:-left-6 md:after:-left-8 after:w-0.5 after:h-[50px] md:after:h-[70px] xl:after:h-[100px] after:bg-dark-shade1">
                  <img loading="eager" fetchPriority="high" src="/images/partners-logos/citi.webp" alt="Citi" className="max-w-full max-h-full w-auto h-auto object-contain -mt-[15px]" width="136" height="70" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThankYou;

